import { useContext, useLayoutEffect, useRef } from 'react';

import { AgentChatHostContext } from '../model';
import type { AgentChatHostProps } from '../model';

// Pages supply editor actions to the persistent chat; they never own a thread.
export const AgentChatHost = (
  props: AgentChatHostProps & { patientID: string },
) => {
  const { setHost } = useContext(AgentChatHostContext);
  const latest = useRef(props);
  latest.current = props;
  const {
    patientID,
    documentMode,
    documentID,
    documentName,
    selectedDocumentText,
  } = props;

  useLayoutEffect(() => {
    const lifetime = { active: true };
    const sameDocument = () =>
      lifetime.active &&
      latest.current.patientID === patientID &&
      latest.current.documentID === documentID;
    const host: AgentChatHostProps = {
      documentMode,
      documentID,
      documentName,
      selectedDocumentText,
      onDocumentChanged: (id) => {
        if (sameDocument()) latest.current.onDocumentChanged?.(id);
      },
      onOpenDocument: (id) => {
        if (sameDocument()) return latest.current.onOpenDocument?.(id);
      },
      onReplaceDocumentSelection: (text) =>
        sameDocument() &&
        (latest.current.onReplaceDocumentSelection?.(text) ?? false),
      onInsertBelowDocumentSelection: (text) =>
        sameDocument() &&
        (latest.current.onInsertBelowDocumentSelection?.(text) ?? false),
    };
    setHost(host);
    return () => {
      // Responses from a page that has unmounted cannot edit its successor.
      lifetime.active = false;
      setHost((current) => (current === host ? null : current));
    };
  }, [
    patientID,
    documentMode,
    documentID,
    documentName,
    selectedDocumentText,
    setHost,
  ]);

  return null;
};
